import * as React from 'react';
import {
  forwardRef,
  type MouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Box from '@mui/material/Box';
import { classes, matchIsValidTel, MuiTelInput, type MuiTelInputCountry } from 'mui-tel-input';
import { AsYouType, getCountries, getCountryCallingCode, parsePhoneNumberFromString } from 'libphonenumber-js';
import { useTheme } from '@mui/material';
import mobileExamples from 'libphonenumber-js/examples.mobile.json';
import { baseFieldSx, overlaySx, phoneOnlySx } from '../styles.ts';

export type PhoneMode = 'callingCode' | 'national';

export type PhoneInputHandle = {
  onKeyClick: (digit: string) => void;
  onClear: () => void;
};

export type PhoneInputMeta = {
  mode: PhoneMode;
  isValid: boolean;
  e164: string | null;
  country: MuiTelInputCountry;
  nationalDigits: string;
  maxNationalDigits: number;
  phoneValue: string;
};

type PhoneInputProps = {
  country: MuiTelInputCountry;
  nationalDigits: string;
  onCountryChange: (country: MuiTelInputCountry) => void;
  onNationalDigitsChange: (digits: string) => void;
  onMetaChange?: (meta: PhoneInputMeta) => void;

  error?: boolean;
  helperText?: ReactNode;
};

const mobileExampleMap = mobileExamples as Partial<Record<MuiTelInputCountry, string>>;

const getMaxNationalDigits = (country: MuiTelInputCountry) => {
  return mobileExampleMap[country]?.length ?? 15;
};

const formatValue = (country: MuiTelInputCountry, nationalDigits: string) => {
  if (!nationalDigits) return '';
  const cc = getCountryCallingCode(country);
  const e164 = `+${cc}${nationalDigits}`;
  return new AsYouType(country).input(e164);
};

const buildCallingCodeMap = (): Record<string, MuiTelInputCountry[]> => {
  const map: Record<string, MuiTelInputCountry[]> = {};

  for (const c of getCountries()) {
    const iso = c as unknown as MuiTelInputCountry;
    const cc = getCountryCallingCode(c);
    (map[cc] ??= []).push(iso);
  }

  return map;
};

export const PhoneInput = forwardRef<PhoneInputHandle, PhoneInputProps>(
  (
    {
      country,
      nationalDigits,
      onCountryChange,
      onNationalDigitsChange,
      onMetaChange,
      error,
      helperText,
    },
    ref,
  ) => {
    const theme = useTheme();
    const [mode, setMode] = useState<PhoneMode>('national');
    const [callingDigits, setCallingDigits] = useState<string>('');

    const callingCodeToCountries = useMemo(() => buildCallingCodeMap(), []);
    const maxNationalDigits = useMemo(() => getMaxNationalDigits(country), [country]);

    const phoneValue = useMemo(
      () => formatValue(country, nationalDigits),
      [country, nationalDigits],
    );

    const inputValue = useMemo(() => {
      return mode === 'callingCode' ? '' : phoneValue;
    }, [mode, phoneValue]);

    const callingOverlay = useMemo(() => {
      if (mode !== 'callingCode') return undefined;
      return callingDigits ? `+${callingDigits}` : '+';
    }, [mode, callingDigits]);

    const placeholder = mode === 'callingCode' ? 'ENTER COUNTRY CODE' : 'ENTER PHONE NUMBER';

    const rootRef = useRef<HTMLDivElement | null>(null);
    const overlayRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);

    const pickCountryForCallingCode = useCallback(
      (cc: string) => {
        const matches = callingCodeToCountries[cc];
        if (!matches?.length) return null;

        if (matches.includes(country)) return country;
        if (matches.includes('US' as MuiTelInputCountry)) return 'US' as MuiTelInputCountry;

        return matches[0];
      },
      [callingCodeToCountries, country],
    );

    const beginCountryCodeEdit = useCallback(() => {
      setMode('callingCode');
      setCallingDigits('');
      onNationalDigitsChange('');
    }, [onNationalDigitsChange]);


    // Publish meta to the page
    useEffect(() => {
      if (!onMetaChange) return;

      const parsed = parsePhoneNumberFromString(phoneValue);
      onMetaChange({
        mode,
        isValid: matchIsValidTel(phoneValue),
        e164: parsed?.number ?? null,
        country,
        nationalDigits,
        maxNationalDigits,
        phoneValue,
      });
    }, [onMetaChange, mode, country, nationalDigits, maxNationalDigits, phoneValue]);

    // Overlay positioning (DOM sync)
    const recalcOverlay = useCallback(() => {
      const root = rootRef.current;
      const overlay = overlayRef.current;
      if (!root || !overlay) return;

      const wrapRect = root.getBoundingClientRect();
      const ccEl =
        (root.querySelector(`.${classes.callingCode}`) as HTMLElement | null) ??
        (root.querySelector('.MuiTelInput-Typography-calling-code') as HTMLElement | null) ??
        (root.querySelector(
          '.MuiInputAdornment-positionStart .MuiTypography-root',
        ) as HTMLElement | null);

      if (!ccEl) {
        overlay.style.visibility = 'hidden';
        return;
      }

      const r = ccEl.getBoundingClientRect();
      overlay.style.left = `${r.left - wrapRect.left}px`;
      overlay.style.top = `${r.top - wrapRect.top}px`;
      overlay.style.height = `${r.height}px`;
      overlay.style.lineHeight = `${r.height}px`;
      overlay.style.visibility = 'visible';
    }, []);

    useLayoutEffect(() => {
      if (mode !== 'callingCode') return;
      recalcOverlay();
    }, [mode, callingOverlay, recalcOverlay]);

    useEffect(() => {
      const onResize = () => recalcOverlay();
      window.addEventListener('resize', onResize);

      const ro =
        typeof ResizeObserver !== 'undefined'
          ? new ResizeObserver(() => recalcOverlay())
          : null;

      if (ro && rootRef.current) ro.observe(rootRef.current);

      return () => {
        window.removeEventListener('resize', onResize);
        ro?.disconnect();
      };
    }, [recalcOverlay]);

    // Enforce controlled input behavior (use custom Keyboard)
    useEffect(() => {
      const el = inputRef.current;
      if (!el) return;

      el.readOnly = true; // Keep all typing via onKeyClick/onClear
      el.style.caretColor = 'transparent';
    }, []);

    // Click on the whole left area: flag + calling code + paddings
    const handleLeftAreaMouseDownCapture = (e: MouseEvent) => {
      if (mode === 'callingCode') return;

      const target = e.target as HTMLElement | null;
      if (!target) return;

      const startAdornment = target.closest('.MuiInputAdornment-positionStart');
      if (!startAdornment) return;

      e.preventDefault();
      e.stopPropagation();
      beginCountryCodeEdit();
    };

    // Expose API for Keyboard
    useImperativeHandle(
      ref,
      () => ({
        onKeyClick: (digit: string) => {
          if (mode === 'callingCode') {
            setCallingDigits((prev) => {
              // Build next calling code (max 3 digits)
              const next = (prev + digit).replace(/\D/g, '').slice(0, 3);

              // Try resolve right away
              const resolved = pickCountryForCallingCode(next);
              if (resolved) {
                onCountryChange(resolved);
                onNationalDigitsChange('');
                setMode('national'); // <-- no ESLint warning (not inside effect)
                return '';
              }

              return next;
            });

            return;
          }

          if (nationalDigits.length >= maxNationalDigits) return;
          onNationalDigitsChange(`${nationalDigits}${digit}`);
        },

        onClear: () => {
          if (mode === 'callingCode') {
            setCallingDigits((prev) => prev.slice(0, -1));
            return;
          }
          onNationalDigitsChange(nationalDigits.slice(0, -1));
        },
      }),
      [
        mode,
        nationalDigits,
        maxNationalDigits,
        onNationalDigitsChange,
        pickCountryForCallingCode,
        onCountryChange,
      ],
    );

    return (
      <Box
        ref={rootRef}
        onMouseDownCapture={(e) => handleLeftAreaMouseDownCapture(e)}
        sx={{ position: 'relative' }}
      >
        <MuiTelInput
          fullWidth={true}
          value={inputValue}
          defaultCountry={country}
          forceCallingCode
          focusOnSelectCountry
          inputMode="tel"
          autoComplete="off"
          error={error}
          helperText={helperText}
          placeholder={placeholder}
          inputRef={inputRef}
          onChange={(newValue, info) => {
            // Country selection via UI still works (even when input is readOnly)
            const nextCountry =
              (info?.countryCode as MuiTelInputCountry | undefined) ?? country;

            onCountryChange(nextCountry);

            // If user somehow injects digits via component, keep it consistent and clamped
            const parsed = parsePhoneNumberFromString(newValue);
            const nextNational = (info?.nationalNumber ?? parsed?.nationalNumber ?? '').replace(
              /\D/g,
              '',
            );
            const nextMax = getMaxNationalDigits(nextCountry);

            onNationalDigitsChange(nextNational.slice(0, nextMax));
            setMode('national');
            setCallingDigits('');
          }}
          sx={baseFieldSx(theme, phoneOnlySx(mode))}
        />

        {mode === 'callingCode' && callingOverlay && (
          <Box
            ref={overlayRef}
            sx={overlaySx}
          >
            {callingOverlay}
          </Box>
        )}
      </Box>
    );
  },
);

